import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { listNotifications } from '../api/notifications.api';
import { NotificationDetail } from '../components/NotificationDetail';
import { NotificationListItem } from '../components/NotificationListItem';
import { useNotifications } from '../hooks/useNotifications';
import type { NotificationCategory, NotificationFilter, NotificationItem } from '../types';

const categories: NotificationCategory[] = [
  'SYSTEM',
  'SECURITY',
  'BILLING',
  'PRODUCT_UPDATE',
  'MARKETING',
];

export const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ publicId?: string }>();
  const selectedPublicId = params.publicId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { version, markRead } = useNotifications();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [category, setCategory] = useState<NotificationCategory | undefined>();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadPage = useCallback(
    async (cursor?: string | null) => {
      const isFirstPage = !cursor;
      if (isFirstPage) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const data = await listNotifications({
          cursor: cursor ?? undefined,
          limit: 20,
          filter,
          category,
        });
        setItems((current) => {
          if (isFirstPage) return data.items;
          const seen = new Set(current.map((item) => item.publicId));
          return [...current, ...data.items.filter((item) => !seen.has(item.publicId))];
        });
        setNextCursor(data.nextCursor);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [category, filter]
  );

  useEffect(() => {
    void loadPage(null);
  }, [loadPage, version]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !nextCursor || loading || loadingMore) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        void loadPage(nextCursor);
      }
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadPage, loading, loadingMore, nextCursor]);

  useEffect(() => {
    setNotFound(false);
  }, [selectedPublicId]);

  const handleItemClick = (item: NotificationItem) => {
    void markRead(item.publicId);
    navigate(`/notifications/${item.publicId}`);
  };

  const listContent = (
    <Box className="ambient-surface flex min-h-0 flex-1 flex-col bg-white dark:bg-slate-900">
      <Box className="border-b border-gray-200 px-4 py-3 dark:border-slate-800">
        <Tabs
          value={filter}
          onChange={(_event, value: NotificationFilter) => setFilter(value)}
          aria-label={t('notifications.tabs.label')}
        >
          <Tab value="all" label={t('notifications.tabs.all')} />
          <Tab value="unread" label={t('notifications.tabs.unread')} />
        </Tabs>
        <Box className="mt-3 flex gap-2 overflow-x-auto pb-1">
          <Chip
            label={t('notifications.category.all')}
            color={!category ? 'primary' : 'default'}
            variant={!category ? 'filled' : 'outlined'}
            onClick={() => setCategory(undefined)}
          />
          {categories.map((value) => (
            <Chip
              key={value}
              label={t(`notifications.category.${value}`)}
              color={category === value ? 'primary' : 'default'}
              variant={category === value ? 'filled' : 'outlined'}
              onClick={() => setCategory(value)}
            />
          ))}
        </Box>
      </Box>
      <Box className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <Box className="space-y-3 p-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Box key={index} className="flex gap-3">
                <Skeleton variant="rounded" width={36} height={36} />
                <Box className="flex-1">
                  <Skeleton width="80%" />
                  <Skeleton width="100%" />
                  <Skeleton width="45%" />
                </Box>
              </Box>
            ))}
          </Box>
        ) : items.length === 0 ? (
          <Box className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-6 text-center">
            <Bell className="text-gray-400 dark:text-slate-500" size={32} />
            <Typography className="text-gray-500 dark:text-slate-400">
              {filter === 'unread' ? t('notifications.emptyUnread') : t('notifications.empty')}
            </Typography>
          </Box>
        ) : (
          <>
            {items.map((item) => (
              <NotificationListItem
                key={item.publicId}
                item={item}
                selected={item.publicId === selectedPublicId}
                onClick={handleItemClick}
              />
            ))}
            <div ref={sentinelRef} className="h-6" />
            {loadingMore && (
              <Box className="flex justify-center py-4">
                <CircularProgress size={22} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const detailContent = selectedPublicId ? (
    notFound ? (
      <Box className="flex min-h-[320px] items-center justify-center p-6 text-center">
        <Typography className="text-gray-500 dark:text-slate-400">
          {t('notifications.notFound')}
        </Typography>
      </Box>
    ) : (
      <NotificationDetail
        key={selectedPublicId}
        publicId={selectedPublicId}
        onBack={isMobile ? () => navigate('/notifications') : undefined}
        onNotFound={() => setNotFound(true)}
      />
    )
  ) : (
    <Box className="flex h-full min-h-[320px] items-center justify-center p-8 text-center">
      <Typography className="max-w-sm text-gray-500 dark:text-slate-400">
        {t('notifications.placeholder')}
      </Typography>
    </Box>
  );

  if (isMobile && selectedPublicId) {
    return <Box className="min-h-full bg-white dark:bg-slate-900">{detailContent}</Box>;
  }

  return (
    <Box className="h-full p-0 text-gray-900 dark:text-slate-100 md:p-4">
      <Box className="flex h-full overflow-hidden border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900 md:rounded-lg md:border">
        <Box className="flex min-h-[calc(100vh-8rem)] w-full min-w-0 flex-col md:w-[380px] md:shrink-0">
          {listContent}
        </Box>
        <Divider orientation="vertical" flexItem className="hidden md:block" />
        <Box className="hidden min-w-0 flex-1 overflow-y-auto md:block">{detailContent}</Box>
      </Box>
    </Box>
  );
};
